import { useEffect, useRef, useState, type ReactNode } from "react";
import { Archive, Eye, EyeSlash, Lightning, PencilSimple, Sparkle } from "@phosphor-icons/react";
import { useAction, useMutation, useQuery } from "convex/react";
import { api as convexApi } from "../../convex/_generated/api";
import type { Id } from "../../convex/_generated/dataModel";
import type {
  ConsolidationTheme,
  DiscussionArcPoint,
  PanelRecap,
  RecapConsolidationPreviewState,
  RecapCoverageStatus,
  RecapConsolidation,
  RecapSection as RecapSectionType,
  RecapTension,
} from "../../shared/types.ts";
import { normalizeDiscussionArcPoints } from "../../shared/recaps/schema.ts";
import type { RoleAuth } from "../lib/auth.ts";
import { fmtTime } from "../lib/format.ts";
import {
  describeManualRecapGenerationResult,
} from "../lib/recapGenerationFeedback.ts";
import {
  manualSummaryGenerationCopy,
  manualSummaryInfoCopy,
  notificationCopy,
  summaryPreviewReadyCopy,
  summaryPublishedCopy,
  type NotificationMessage,
} from "../lib/notificationCopy.ts";
import { notify } from "../lib/notify.ts";
import { EmptyState } from "./EmptyState.tsx";

type OperatorTab = "timeline" | "consolidated" | "themes";

export function RecapPanel({
  recaps,
  title = "Summaries",
  auth,
  sessionId,
  operator = false,
  operatorPage = "timeline",
  operatorNavigation,
  compact = false,
  coverage = null,
}: {
  recaps: PanelRecap[];
  title?: string;
  auth?: RoleAuth;
  sessionId?: string;
  operator?: boolean;
  operatorPage?: OperatorTab;
  operatorNavigation?: ReactNode;
  compact?: boolean;
  coverage?: RecapCoverageStatus | null;
}) {
  const generateRecap = useAction(convexApi.panelRecapGeneration.generateForActiveSession);
  const setStatus = useMutation(convexApi.panelRecaps.setStatus);
  const [busy, setBusy] = useState<string | null>(null);

  const consolidation = useQuery(
    convexApi.panelRecaps.getConsolidationForAudience,
    sessionId ? { sessionId: sessionId as Id<"sessions"> } : "skip",
  );

  async function forceGenerate() {
    if (!operator || !auth || !sessionId) return;
    setBusy("generate");
    try {
      const result = await generateRecap({
        auth,
        sessionId: sessionId as Id<"sessions">,
        force: true,
      });
      const feedback = describeManualRecapGenerationResult(result);
      if (feedback.kind === "success") notify.success(manualSummaryGenerationCopy(feedback.message, result.provider));
      else if (feedback.kind === "info") notify.info(manualSummaryInfoCopy(feedback.message));
      else notify.error(notificationCopy.admin.couldNotGenerateSummary);
    } catch {
      notify.error(notificationCopy.admin.couldNotGenerateSummary);
    } finally {
      setBusy(null);
    }
  }

  async function updateStatus(recap: PanelRecap, status: PanelRecap["status"]) {
    if (!operator || !auth) return;
    setBusy(`${recap.id}:${status}`);
    try {
      const result = await setStatus({
        auth,
        recapId: recap.id as Id<"panelRecaps">,
        status,
      });
      if (result == null) throw new Error("Summary was not updated.");
      notify.success(recapStatusCopy(status));
    } catch {
      notify.error(notificationCopy.admin.couldNotUpdateSummary);
    } finally {
      setBusy(null);
    }
  }

  return (
    <section className={`panel recap-panel ${compact ? "compact" : ""}`} data-testid="recap-panel">
      <div className="panel-head">
        <h2>{title} <span className="count" data-testid="recap-count">{recaps.length}</span></h2>
        {operator && operatorPage === "timeline" && (
          <button
            type="button"
            className="btn primary sm"
            data-testid="generate-recap-now"
            disabled={busy === "generate"}
            onClick={() => void forceGenerate()}
          >
            <Sparkle weight="fill" /> {busy === "generate" ? "Generating..." : "Generate timeline summaries"}
          </button>
        )}
      </div>

      {operator && operatorNavigation}

      {operator && coverage && ["retrying", "verifying", "blocked", "finalizing"].includes(coverage.phase) && (
        <div
          className={`recap-coverage-notice ${coverage.phase}`}
          data-testid="recap-coverage-notice"
          role={coverage.phase === "blocked" ? "alert" : "status"}
        >
          <strong>{coverage.phase.replace("_", " ")}</strong>
          <span>{recapCoverageNotice(coverage)}</span>
        </div>
      )}

      {operator && (operatorPage === "consolidated" || operatorPage === "themes") ? (
        <div className="recap-scroll-shell">
          <ConsolidatedView
            consolidation={consolidation ?? null}
            auth={auth}
            sessionId={sessionId}
            busy={busy}
            setBusy={setBusy}
            section={operatorPage === "themes" ? "themes" : "arc"}
          />
        </div>
      ) : (
        <div className="recap-scroll-shell">
          {recaps.length === 0 && (
            <EmptyState size="sm" testid="recap-empty">No summaries yet.</EmptyState>
          )}
          {operator && recaps.length > 0 ? (
            <OperatorTimeline recaps={recaps} auth={auth} busy={busy} onStatusChange={updateStatus} />
          ) : (
            <div className="recap-list">
              {recaps.map((recap) => (
                <RecapCard
                  key={recap.id}
                  recap={recap}
                  operator={false}
                  auth={auth}
                  busy={busy}
                  onStatusChange={updateStatus}
                />
              ))}
            </div>
          )}
        </div>
      )}
    </section>
  );
}

function recapCoverageNotice(coverage: RecapCoverageStatus): string {
  if (coverage.phase === "blocked") {
    return coverage.errorSummary ||
      coverage.integrityCodes.join(", ") ||
      "Timeline coverage needs operator attention.";
  }
  if (coverage.phase === "verifying") {
    return `${coverage.verificationWindowCount}${coverage.countsClamped ? "+" : ""} corrected window(s) awaiting verification.`;
  }
  if (coverage.phase === "retrying") {
    return `${coverage.retryableWindowCount}${coverage.countsClamped ? "+" : ""} timeline window(s) scheduled to retry.`;
  }
  return `Session coverage is ${coverage.finalizationStatus ?? "being finalized"}.`;
}

function OperatorTimeline({
  recaps,
  auth,
  busy,
  onStatusChange,
}: {
  recaps: PanelRecap[];
  auth?: RoleAuth;
  busy: string | null;
  onStatusChange: (recap: PanelRecap, status: PanelRecap["status"]) => void;
}) {
  const latest = recaps[0];
  const history = recaps.slice(1).reverse();

  return (
    <div className="recap-list">
      <RecapCard
        key={latest.id}
        recap={latest}
        operator
        auth={auth}
        busy={busy}
        onStatusChange={onStatusChange}
      />
      {history.length > 0 && (
        <>
          <div className="recap-history-divider">Earlier</div>
          {history.map((recap) => (
            <RecapCard
              key={recap.id}
              recap={recap}
              operator
              auth={auth}
              busy={busy}
              onStatusChange={onStatusChange}
            />
          ))}
        </>
      )}
    </div>
  );
}

function RecapCard({
  recap,
  operator,
  auth,
  busy,
  onStatusChange,
}: {
  recap: PanelRecap;
  operator: boolean;
  auth?: RoleAuth;
  busy: string | null;
  onStatusChange: (recap: PanelRecap, status: PanelRecap["status"]) => void;
}) {
  return (
    <article className="recap-card" data-testid="recap-card" key={recap.id}>
      <header className="recap-card-head">
        <div>
          <strong>{fmtTime(recap.createdAt)}</strong>
          <span>{triggerLabel(recap.triggerKind)}</span>
        </div>
        <span className={`badge recap-status ${recap.status}`}>{recap.status}</span>
      </header>

      {recap.sections && recap.sections.length > 0 ? (
        <div className="recap-sections">
          {recap.sections.map((section, idx) => (
            <SectionCard
              key={`${recap.id}-s${idx}`}
              section={section}
              recapId={recap.id}
              ordinal={idx}
              editable={operator}
              auth={auth}
            />
          ))}
        </div>
      ) : (
        <LegacyRecapBody recap={recap} />
      )}

      {operator && (
        <footer className="recap-actions">
          <button
            type="button"
            className="btn ghost sm"
            disabled={recap.status === "shown" || busy !== null}
            onClick={() => onStatusChange(recap, "shown")}
          >
            <Eye weight="bold" /> Show
          </button>
          <button
            type="button"
            className="btn ghost sm"
            disabled={recap.status === "draft" || busy !== null}
            onClick={() => onStatusChange(recap, "draft")}
          >
            <EyeSlash weight="bold" /> Hide
          </button>
          <button
            type="button"
            className="btn danger sm"
            disabled={recap.status === "archived" || busy !== null}
            onClick={() => onStatusChange(recap, "archived")}
          >
            <Archive weight="bold" /> Archive
          </button>
        </footer>
      )}
    </article>
  );
}

function ConsolidatedView({
  consolidation,
  auth,
  sessionId,
  busy,
  setBusy,
  section = "arc",
}: {
  consolidation: RecapConsolidation | null;
  auth?: RoleAuth;
  sessionId?: string;
  busy: string | null;
  setBusy: (v: string | null) => void;
  /** "arc" hosts the preview/publish machinery (a preview is reviewed as a
   *  full snapshot); "themes" is a read-only view of the published themes. */
  section?: "arc" | "themes";
}) {
  const previewAction = useAction(convexApi.panelRecapGeneration.previewConsolidation);
  const publishMutation = useMutation(convexApi.panelRecaps.publishConsolidation);
  const rejectMutation = useMutation(convexApi.panelRecaps.rejectConsolidationPreview);
  const previewState = useQuery(
    convexApi.panelRecaps.getPendingConsolidationPreview,
    auth && sessionId
      ? { auth, sessionId: sessionId as Id<"sessions"> }
      : "skip",
  ) as RecapConsolidationPreviewState | undefined;
  const [consolidateResult, setConsolidateResult] = useState<string | null>(null);
  const [viewMode, setViewMode] = useState<"current" | "preview">("current");
  const lastPreviewId = useRef<string | null>(null);
  const preview = previewState?.preview ?? null;
  const previewLoading = previewState === undefined;
  const previewGenerating = previewState?.status === "generating" ||
    previewState?.status === "regenerating";

  useEffect(() => {
    const previewId = preview?.id ?? null;
    if (previewId && previewId !== lastPreviewId.current) setViewMode("preview");
    if (!previewId) setViewMode("current");
    lastPreviewId.current = previewId;
  }, [preview?.id]);

  async function runPreview() {
    if (!auth || !sessionId) return;
    setBusy("consolidate");
    setConsolidateResult(null);
    try {
      const result = await previewAction({
        auth,
        sessionId: sessionId as Id<"sessions">,
      });
      if (result.outcome === "ready") {
        notify.success(summaryPreviewReadyCopy({
          inputSectionCount: result.inputSectionCount ?? 0,
          themeCount: result.themeCount ?? 0,
          narrativeCount: result.narrativeCount ?? 0,
        }));
      } else {
        setConsolidateResult(result.reason ?? "Nothing to consolidate.");
      }
    } catch {
      notify.error(notificationCopy.admin.couldNotConsolidate);
    } finally {
      setBusy(null);
    }
  }

  async function handlePublish() {
    if (!auth || !sessionId || !preview) return;
    setBusy("publish");
    try {
      const result = await publishMutation({
        auth,
        sessionId: sessionId as Id<"sessions">,
        previewId: preview.id as Id<"recapConsolidationPreviews">,
      });
      if (result == null) throw new Error("Consolidation was not published.");
      setViewMode("current");
      notify.success(summaryPublishedCopy({
        inputSectionCount: preview.inputSectionCount,
        themeCount: preview.themes.length,
      }));
    } catch {
      notify.error(notificationCopy.admin.couldNotPublish);
    } finally {
      setBusy(null);
    }
  }

  async function handleDiscard() {
    if (!auth || !sessionId || !preview) return;
    setBusy("reject");
    try {
      await rejectMutation({
        auth,
        sessionId: sessionId as Id<"sessions">,
        previewId: preview.id as Id<"recapConsolidationPreviews">,
      });
      setViewMode("current");
    } catch {
      notify.error({ title: "Could not discard preview." });
    } finally {
      setBusy(null);
    }
  }

  if (section === "themes") {
    const themes = consolidation?.kind === "thematic" ? consolidation.themes : [];
    return (
      <div className="recap-consolidated" data-testid="operator-themes-view">
        {consolidation && (
          <div className="recap-consolidated-meta">
            <span>Consolidated {fmtTime(consolidation.consolidatedAt)}</span>
          </div>
        )}
        {themes.length > 0 ? (
          <div className="consolidation-themes" data-testid="consolidation-themes">
            {themes.map((theme, i) => <ThemeCard key={i} theme={theme} />)}
          </div>
        ) : (
          <EmptyState size="sm" testid="operator-themes-empty">No themes yet.</EmptyState>
        )}
      </div>
    );
  }

  if (!consolidation && !preview) {
    return (
      <div className="recap-consolidated-empty">
        <EmptyState size="sm" testid="consolidated-empty">No consolidated summary yet.</EmptyState>
        <button
          type="button"
          className="btn primary sm"
          data-testid="consolidate-now"
          disabled={busy === "consolidate" || previewLoading || previewGenerating}
          onClick={() => void runPreview()}
        >
          <Lightning weight="fill" /> {busy === "consolidate" || previewGenerating ? "Generating..." : "Generate consolidated draft"}
        </button>
        {consolidateResult && <div className="recap-consolidate-info" role="status">{consolidateResult}</div>}
      </div>
    );
  }

  return (
    <div className="recap-consolidated" data-testid="consolidated-view">
      <div className="recap-consolidated-meta">
        {consolidation ? (
          <>
            <span>Consolidated {fmtTime(consolidation.consolidatedAt)}</span>
            <span>{consolidation.inputSectionCount} sections merged</span>
          </>
        ) : (
          <span>No published consolidated summary yet</span>
        )}
        {!preview && (
          <button
            type="button"
            className="btn ghost sm"
            data-testid="consolidate-now"
            disabled={busy !== null || previewLoading || previewGenerating}
            onClick={() => void runPreview()}
          >
            <Lightning weight="fill" /> {busy === "consolidate" || previewGenerating ? "Generating..." : "Generate consolidated draft"}
          </button>
        )}
      </div>
      {consolidateResult && <div className="recap-consolidate-info" role="status">{consolidateResult}</div>}

      {consolidation?.staleAt && (
        <div className="recap-consolidate-info" role="status" data-testid="consolidation-stale-banner">
          Stale — {consolidation.staleReason ?? "source material was corrected"}. Generate a new consolidated draft to update.
        </div>
      )}

      {consolidation?.freshness?.behindLatestRecap && (
        <div className="recap-consolidate-info" role="status" data-testid="consolidation-behind-banner">
          Behind timeline: {freshnessLabel(consolidation.freshness.newerSectionCountLabel)} newer summary material is not consolidated yet.
        </div>
      )}

      {preview && (
        <div className="consolidation-preview-bar" data-testid="consolidation-preview-bar">
          <div className="consolidation-preview-tabs">
            <button
              type="button"
              className={viewMode === "current" ? "active" : ""}
              onClick={() => setViewMode("current")}
            >Current</button>
            <button
              type="button"
              className={viewMode === "preview" ? "active" : ""}
              onClick={() => setViewMode("preview")}
            >Preview</button>
          </div>
          <div className="consolidation-preview-actions">
            <button
              type="button"
              className="consolidation-discard-btn"
              disabled={busy !== null || previewGenerating}
              onClick={() => void runPreview()}
            ><Lightning weight="fill" /> {previewGenerating ? "Regenerating..." : "Regenerate"}</button>
            <button
              type="button"
              className="consolidation-publish-btn"
              data-testid="consolidation-publish"
              disabled={busy !== null || previewGenerating || preview.stale}
              onClick={() => void handlePublish()}
            >{busy === "publish" ? "Publishing..." : "Confirm & publish"}</button>
            <button
              type="button"
              className="consolidation-discard-btn"
              data-testid="consolidation-discard"
              disabled={busy === "publish" || busy === "reject"}
              onClick={() => void handleDiscard()}
            >Discard</button>
          </div>
        </div>
      )}

      {preview?.stale && (
        <div className="recap-consolidate-info" role="alert" data-testid="consolidation-preview-stale-banner">
          This preview is stale because its source attribution or consolidation changed. Regenerate or discard it.
        </div>
      )}

      {viewMode === "preview" && preview ? (
        // A preview is a full replacement snapshot — review it whole.
        <ThematicContent narrative={preview.narrative} themes={preview.themes} section="both" />
      ) : consolidation ? (
        <ConsolidationContent consolidation={consolidation} />
      ) : (
        <EmptyState size="sm" testid="consolidated-current-empty">No consolidated summary has been published.</EmptyState>
      )}
    </div>
  );
}

function freshnessLabel(label: string | null | undefined): string {
  return label ? `${label} section${label === "1" ? "" : "s"} of` : "some";
}

function recapStatusCopy(status: PanelRecap["status"]): NotificationMessage {
  if (status === "shown") return notificationCopy.admin.summaryShown;
  if (status === "draft") return notificationCopy.admin.summaryHidden;
  return notificationCopy.admin.summaryArchived;
}

function ConsolidationContent({ consolidation }: { consolidation: RecapConsolidation }) {
  if (consolidation.kind === "thematic") {
    return <ThematicContent narrative={consolidation.narrative} themes={consolidation.themes} section="arc" />;
  }
  return <LegacyConsolidationContent consolidation={consolidation} />;
}

function ThematicContent({
  narrative,
  themes,
  section = "both",
}: {
  narrative: DiscussionArcPoint[];
  themes: ConsolidationTheme[];
  section?: "arc" | "both";
}) {
  const arc = normalizeDiscussionArcPoints(narrative);
  return (
    <>
      <div className="consolidation-narrative" data-testid="consolidation-narrative">
        {section === "both" && <h3 className="consolidation-section-heading">Discussion arc</h3>}
        <ul className="consolidation-narrative-list">
          {arc.map((item, i) => (
            <li key={`${item.point}-${i}`}>
              <span className="consolidation-narrative-point">{item.point}</span>
              {item.subpoints.length > 0 && (
                <ul className="consolidation-narrative-subpoints">
                  {item.subpoints.map((subpoint, subpointIndex) => (
                    <li key={`${subpoint}-${subpointIndex}`}>{subpoint}</li>
                  ))}
                </ul>
              )}
            </li>
          ))}
        </ul>
      </div>
      {section === "both" && (
        <div className="consolidation-themes" data-testid="consolidation-themes">
          <h3 className="consolidation-section-heading">Key themes</h3>
          {themes.map((theme, i) => <ThemeCard key={i} theme={theme} />)}
        </div>
      )}
    </>
  );
}

function ThemeCard({ theme }: { theme: ConsolidationTheme }) {
  return (
    <div className="consolidation-theme-card" data-testid="theme-card">
      <h4 className="consolidation-theme-title">{theme.title}</h4>
      <div className="consolidation-theme-speakers">{theme.speakers.join(", ")}</div>
      <p className="consolidation-theme-synthesis">{theme.synthesis}</p>
      {theme.tensions.length > 0 && (
        <div className="consolidation-theme-tensions">
          {theme.tensions.map((tension, ti) => (
            <div key={ti} className="consolidation-theme-tension">
              <span className="consolidation-tension-label">Tension:</span>
              <span>{tension.description}</span>
              <span className="consolidation-tension-speakers">{tension.speakers.join(" vs ")}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function LegacyConsolidationContent({ consolidation }: { consolidation: RecapConsolidation }) {
  return (
    <>
      <div className="recap-sections">
        {consolidation.sections.map((section, idx) => (
          <SectionCard
            key={`consolidated-s${idx}`}
            section={section}
            recapId=""
            ordinal={idx}
            editable={false}
          />
        ))}
      </div>
      {consolidation.tensions.length > 0 && (
        <div className="recap-tensions" data-testid="tensions-list">
          <h3 className="recap-tensions-heading">Tensions</h3>
          {consolidation.tensions.map((tension, idx) => (
            <TensionCard key={idx} tension={tension} />
          ))}
        </div>
      )}
    </>
  );
}

function TensionCard({ tension }: { tension: RecapTension }) {
  return (
    <div className="recap-tension-card" data-testid="tension-card">
      <p className="recap-tension-desc">{tension.description}</p>
      <div className="recap-tension-meta">
        <span className="recap-tension-speakers">{tension.speakers.join(" vs ")}</span>
        <span className="recap-tension-time">{tension.timeRange}</span>
      </div>
    </div>
  );
}

function SectionCard({
  section,
  recapId,
  ordinal,
  editable,
  auth,
}: {
  section: RecapSectionType;
  recapId: string;
  ordinal: number;
  editable: boolean;
  auth?: RoleAuth;
}) {
  const editSpeaker = useMutation(convexApi.panelRecaps.editSectionSpeaker);
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(section.speaker);
  const [saving, setSaving] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  async function commitEdit() {
    const trimmed = draft.trim();
    if (!trimmed || trimmed === section.speaker || !auth) {
      setEditing(false);
      setDraft(section.speaker);
      return;
    }
    setSaving(true);
    try {
      await editSpeaker({
        auth,
        recapId: recapId as Id<"panelRecaps">,
        ordinal,
        speaker: trimmed,
      });
    } finally {
      setSaving(false);
      setEditing(false);
    }
  }

  function startEdit() {
    if (!editable || !auth) return;
    setDraft(section.speaker);
    setEditing(true);
    requestAnimationFrame(() => inputRef.current?.select());
  }

  return (
    <div className="recap-section-card" data-testid="recap-section">
      <div className="recap-section-head">
        <span className="recap-section-time">{section.timeLabel}</span>
        {editing ? (
          <input
            ref={inputRef}
            className="recap-speaker-input"
            value={draft}
            disabled={saving}
            onChange={(e) => setDraft(e.target.value)}
            onBlur={() => void commitEdit()}
            onKeyDown={(e) => {
              if (e.key === "Enter") void commitEdit();
              if (e.key === "Escape") { setEditing(false); setDraft(section.speaker); }
            }}
            data-testid="recap-speaker-edit"
          />
        ) : (
          <span
            className={`recap-section-speaker ${editable ? "editable" : ""}`}
            role={editable ? "button" : undefined}
            tabIndex={editable ? 0 : undefined}
            onClick={startEdit}
            onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") startEdit(); }}
          >
            {section.speaker}
            {editable && <PencilSimple weight="bold" className="recap-speaker-edit-icon" />}
            {section.speakerEdited && <span className="recap-speaker-edited-badge">edited</span>}
          </span>
        )}
      </div>
      <ul className="recap-section-points">
        {section.points.map((point, pi) => (
          <li key={pi}>
            {point.text}
            {point.subpoints.length > 0 && (
              <ul className="recap-subpoints">
                {point.subpoints.map((sub, si) => (
                  <li key={si}>{sub}</li>
                ))}
              </ul>
            )}
          </li>
        ))}
      </ul>
    </div>
  );
}

function LegacyRecapBody({ recap }: { recap: PanelRecap }) {
  return (
    <>
      <LegacySection title="Key points" items={recap.keyPoints} />
      <LegacySection title="Examples raised" items={recap.examples} />
      {recap.differentViews && recap.differentViews.length > 0 && (
        <LegacySection title="Different views" items={recap.differentViews} />
      )}
    </>
  );
}

function LegacySection({ title, items }: { title: string; items?: string[] }) {
  if (!items || items.length === 0) return null;
  return (
    <section className="recap-section">
      <h3>{title}</h3>
      <ul>
        {items.map((item, index) => <li key={`${title}-${index}`}>{item}</li>)}
      </ul>
    </section>
  );
}

function triggerLabel(trigger: PanelRecap["triggerKind"]): string {
  if (trigger === "long-turn") return "long turn";
  if (trigger === "ongoing") return "ongoing";
  if (trigger === "manual") return "manual";
  return "threshold";
}
